// Social Stats Component
import React, { useState, useEffect } from 'react';
import { TrendingUp, Heart, MessageCircle, Share2, Eye, BarChart3 } from 'lucide-react';
import { SocialStats as SocialStatsType, SocialPost } from '../types/social.types';
import { socialMediaService } from '../services/socialMediaService';

const SocialStats: React.FC = () => {
  const [dateRange, setDateRange] = useState({ from: '', to: '' });
  const [stats, setStats] = useState<{
    totalPosts: number;
    totalReach: number;
    totalEngagement: number;
    avgEngagementRate: number;
    topPerformingPosts: SocialPost[];
  } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Ustaw domyślny zakres dat (ostatnie 30 dni)
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30);
    
    setDateRange({
      from: from.toISOString().split('T')[0],
      to: to.toISOString().split('T')[0]
    });
  }, []);

  useEffect(() => {
    if (dateRange.from && dateRange.to) {
      loadStats();
    }
  }, [dateRange]);

  const loadStats = async () => {
    setLoading(true);
    try {
      const aggregatedStats = await socialMediaService.getAggregatedStats(
        new Date(dateRange.from),
        new Date(dateRange.to)
      );
      setStats(aggregatedStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}k`;
    return num.toString();
  };

  const getEngagementColor = (rate: number): string => {
    if (rate >= 5) return 'text-green-400';
    if (rate >= 2) return 'text-blue-400';
    return 'text-gray-400';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filtry dat */}
      <div className="bg-zinc-800 rounded-lg shadow-xl p-4 border border-zinc-700">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Statystyki Social Media</h2>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={dateRange.from}
              onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
              className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-white"
            />
            <span className="text-gray-400">do</span>
            <input
              type="date"
              value={dateRange.to}
              onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
              className="px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-lg text-sm text-white"
            />
          </div>
        </div>
      </div>

      {stats && (
        <>
          {/* Karty ze statystykami */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
              <div className="flex items-center justify-between mb-2">
                <BarChart3 className="w-8 h-8 text-blue-500" />
                <span className="text-2xl font-bold text-white">{stats.totalPosts}</span>
              </div>
              <p className="text-gray-300">Opublikowane posty</p>
              <p className="text-sm text-gray-500 mt-1">
                {Math.round(stats.totalPosts / 30)} dziennie
              </p>
            </div>

            <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-8 h-8 text-purple-500" />
                <span className="text-2xl font-bold text-white">
                  {formatNumber(stats.totalReach)}
                </span>
              </div>
              <p className="text-gray-300">Całkowity zasięg</p>
              <p className="text-sm text-gray-500 mt-1">
                {formatNumber(Math.round(stats.totalReach / stats.totalPosts))} na post
              </p>
            </div>

            <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
              <div className="flex items-center justify-between mb-2">
                <Heart className="w-8 h-8 text-red-500" />
                <span className="text-2xl font-bold text-white">
                  {formatNumber(stats.totalEngagement)}
                </span>
              </div>
              <p className="text-gray-300">Zaangażowanie</p>
              <p className="text-sm text-gray-500 mt-1">
                polubienia, udostępnienia, komentarze
              </p>
            </div>

            <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-8 h-8 text-green-500" />
                <span className={`text-2xl font-bold ${getEngagementColor(stats.avgEngagementRate)}`}>
                  {stats.avgEngagementRate}%
                </span>
              </div>
              <p className="text-gray-300">Średnie zaangażowanie</p>
              <p className="text-sm text-gray-500 mt-1">
                {stats.avgEngagementRate >= 2 ? 'Dobry wynik!' : 'Można poprawić'}
              </p>
            </div>
          </div>

          {/* Top posty */}
          {stats.topPerformingPosts.length > 0 && (
            <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
              <h3 className="text-lg font-semibold mb-4 text-white">
                🏆 Najlepsze posty
              </h3>
              <div className="space-y-4">
                {stats.topPerformingPosts.map((post, index) => (
                  <div key={post.id} className="flex items-start gap-4 p-4 bg-zinc-900 rounded-lg border border-zinc-700">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-600/20 rounded-full flex items-center justify-center font-bold text-blue-400">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-300 line-clamp-2">
                        {post.content}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span>
                          {new Date(post.published_at || post.created_at).toLocaleDateString('pl-PL')}
                        </span>
                        <span className="flex items-center gap-1">
                          {post.platforms.map(p => (
                            <span key={p} className="capitalize">
                              {p === 'facebook' && '📘'}
                              {p === 'linkedin' && '💼'}
                              {p === 'instagram' && '📸'}
                              {p === 'twitter' && '🐦'}
                            </span>
                          ))}
                        </span>
                      </div>
                    </div>
                    <button className="text-blue-400 hover:text-blue-300 text-sm transition-all">
                      Zobacz szczegóły →
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Wykres zaangażowania (placeholder) */}
          <div className="bg-zinc-800 rounded-lg shadow-xl p-6 border border-zinc-700">
            <h3 className="text-lg font-semibold mb-4 text-white">
              📊 Zaangażowanie w czasie
            </h3>
            <div className="h-64 bg-zinc-900 rounded-lg flex items-center justify-center text-gray-500 border border-zinc-700">
              <p>Wykres zaangażowania (wymaga integracji z biblioteką wykresów)</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SocialStats;
